import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import analyticsService, {
  AnalyticsOverview,
  MailboxAnalytics,
  ReplyIntentDistribution,
  WarmupTelemetryResponse,
  RotationOverviewSummary,
  RotationOverviewCampaign,
  RotationHealthSummary,
  RotationMailboxStat
} from '../../../services/analyticsService';
import emailService, { EmailMailbox } from '../../../services/emailService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { format } from 'date-fns';
import ABTestResults from './ABTestResults';
import EmailDeliverabilityPanel from './EmailDeliverabilityPanel';
import WarmupHealthPanel from './WarmupHealthPanel';
import TrackingDomainsPanel from './TrackingDomainsPanel';
import DomainHealthPanel from './DomainHealthPanel';

const EmailAnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  // New states for mailbox and intent analytics
  const [mailboxes, setMailboxes] = useState<EmailMailbox[]>([]);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string>('');
  const [mailboxAnalytics, setMailboxAnalytics] = useState<MailboxAnalytics | null>(null);
  const [intentDist, setIntentDist] = useState<ReplyIntentDistribution | null>(null);
  const [mailboxLoading, setMailboxLoading] = useState<boolean>(false);
  const [intentLoading, setIntentLoading] = useState<boolean>(false);
  const [premiumBlocked, setPremiumBlocked] = useState<boolean>(false);
  const [warmupLoading, setWarmupLoading] = useState<boolean>(false);
  const [warmupTelemetry, setWarmupTelemetry] = useState<WarmupTelemetryResponse | null>(null);
  const [selectedRotationCampaignId, setSelectedRotationCampaignId] = useState<string | null>(null);
  const [selectedRotationCampaignName, setSelectedRotationCampaignName] = useState<string>('');
  const [rotationHealth, setRotationHealth] = useState<RotationHealthSummary | null>(null);
  const [rotationHealthLoading, setRotationHealthLoading] = useState<boolean>(false);
  const [rotationHealthError, setRotationHealthError] = useState<string | null>(null);
  const [assignmentMutations, setAssignmentMutations] = useState<Record<string, boolean>>({});
  const [capDrafts, setCapDrafts] = useState<Record<string, string>>({});
  const [rotationActionMessage, setRotationActionMessage] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set active tab based on URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'abtestresults' || tab === 'abtesting' || tab === '1') {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getAnalyticsOverview();
        setAnalytics(data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load analytics data');
        console.error('Error loading analytics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  // Load mailboxes and default select first
  useEffect(() => {
    const loadMailboxes = async () => {
      try {
        const m = await emailService.getMailboxes();
        setMailboxes(m);
        if (m.length > 0 && !selectedMailboxId) setSelectedMailboxId(m[0].id as string);
      } catch (e) {
        console.error('Failed to load mailboxes', e);
      }
    };
    loadMailboxes();
  }, [selectedMailboxId]);

  // Fetch mailbox analytics when selection changes
  useEffect(() => {
    const loadMailboxAnalytics = async () => {
      if (!selectedMailboxId) return;
      try {
        setPremiumBlocked(false);
        setMailboxLoading(true);
        const res = await analyticsService.getMailboxAnalytics(selectedMailboxId);
        setMailboxAnalytics(res);
      } catch (e: any) {
        console.error('Failed to load mailbox analytics', e);
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
          setPremiumBlocked(true);
        }
      }
      finally {
        setMailboxLoading(false);
      }
    };
    loadMailboxAnalytics();
  }, [selectedMailboxId]);

  // Fetch warmup telemetry when mailbox changes
  useEffect(() => {
    const loadWarmup = async () => {
      if (!selectedMailboxId) return;
      try {
        setPremiumBlocked(false);
        setWarmupLoading(true);
        const res = await analyticsService.getWarmupTelemetry(selectedMailboxId);
        setWarmupTelemetry(res);
      } catch (e: any) {
        console.error('Failed to load warmup telemetry', e);
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
          setPremiumBlocked(true);
        }
        setWarmupTelemetry(null);
      } finally {
        setWarmupLoading(false);
      }
    };
    loadWarmup();
  }, [selectedMailboxId]);

  // Fetch reply intent distribution
  useEffect(() => {
    const loadIntent = async () => {
      try {
        setPremiumBlocked(false);
        setIntentLoading(true);
        const res = await analyticsService.getReplyIntentDistribution();
        setIntentDist(res);
      } catch (e) {
        console.error('Failed to load reply intent distribution', e);
        const msg = (e as any)?.message?.toLowerCase?.() || '';
        if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
          setPremiumBlocked(true);
        }
      }
      finally {
        setIntentLoading(false);
      }
    };
    loadIntent();
  }, []);

  const initializeCapDrafts = (summary: RotationHealthSummary | null) => {
    if (!summary) {
      setCapDrafts({});
      return;
    }
    const drafts: Record<string, string> = {};
    summary.mailboxes.forEach(mailbox => {
      if (mailbox.assignment_id) {
        drafts[mailbox.assignment_id] = mailbox.assignment_daily_cap !== null && mailbox.assignment_daily_cap !== undefined
          ? String(mailbox.assignment_daily_cap)
          : '';
      }
    });
    setCapDrafts(drafts);
  };

  const loadRotationHealth = async (campaignId: string, campaignName: string) => {
    setSelectedRotationCampaignId(campaignId);
    setSelectedRotationCampaignName(campaignName);
    setRotationHealthLoading(true);
    setRotationHealthError(null);
    try {
      const campaignAnalytics = await analyticsService.getCampaignAnalytics(campaignId);
      const summary = campaignAnalytics.rotation_health || null;
      setRotationHealth(summary);
      initializeCapDrafts(summary);
    } catch (error: any) {
      console.error('Failed to load rotation health', error);
      setRotationHealthError(error?.message || 'Failed to load rotation health');
    } finally {
      setRotationHealthLoading(false);
    }
  };

  const mutationKeyFor = (mailbox: RotationMailboxStat) => mailbox.assignment_id || mailbox.mailbox_id;

  const patchRotationMailbox = (assignmentId: string, patch: Partial<RotationMailboxStat>) => {
    setRotationHealth(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        mailboxes: prev.mailboxes.map(mailbox =>
          mailbox.assignment_id === assignmentId ? { ...mailbox, ...patch } : mailbox
        )
      };
    });
  };

  const handleAssignmentStatusChange = async (
    mailbox: RotationMailboxStat,
    nextStatus: 'active' | 'paused'
  ) => {
    if (!selectedRotationCampaignId || !mailbox.assignment_id) {
      return;
    }
    const assignmentId = mailbox.assignment_id;
    setRotationHealthError(null);
    setAssignmentMutations(prev => ({ ...prev, [assignmentId]: true }));
    try {
      await emailService.updateCampaignMailboxAssignment(selectedRotationCampaignId, assignmentId, { status: nextStatus });
      patchRotationMailbox(assignmentId, { assignment_status: nextStatus });
      setRotationActionMessage(`Mailbox ${mailbox.mailbox_email} ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
    } catch (error: any) {
      console.error('Failed to update assignment status', error);
      setRotationHealthError(error?.message || 'Failed to update mailbox assignment');
    } finally {
      setAssignmentMutations(prev => ({ ...prev, [assignmentId]: false }));
      setTimeout(() => setRotationActionMessage(null), 4000);
    }
  };

  const handleCapDraftChange = (assignmentId: string, value: string) => {
    setCapDrafts(prev => ({ ...prev, [assignmentId]: value }));
  };

  const handleDailyCapSave = async (mailbox: RotationMailboxStat) => {
    if (!selectedRotationCampaignId || !mailbox.assignment_id) {
      return;
    }
    const assignmentId = mailbox.assignment_id;
    const draftValue = capDrafts[assignmentId];
    const parsedValue = draftValue === '' || draftValue === undefined ? null : Math.max(0, Number(draftValue));

    setRotationHealthError(null);
    setAssignmentMutations(prev => ({ ...prev, [assignmentId]: true }));
    try {
      await emailService.updateCampaignMailboxAssignment(selectedRotationCampaignId, assignmentId, { daily_cap: parsedValue });
      patchRotationMailbox(assignmentId, { assignment_daily_cap: parsedValue });
      setRotationActionMessage(`Daily cap updated for ${mailbox.mailbox_email}.`);
    } catch (error: any) {
      console.error('Failed to update assignment cap', error);
      setRotationHealthError(error?.message || 'Failed to update daily cap');
    } finally {
      setAssignmentMutations(prev => ({ ...prev, [assignmentId]: false }));
      setTimeout(() => setRotationActionMessage(null), 4000);
    }
  };

  // Format daily activity data for charts
  const formatDailyData = () => {
    if (!analytics?.daily_activity) return [];
    
    return analytics.daily_activity.map(day => ({
      date: format(new Date(day.date), 'MMM dd'),
      sent: day.emails_sent,
      opened: day.emails_opened,
      clicked: day.emails_clicked,
      replied: day.emails_replied
    }));
  };

  // Format metrics data for the pie chart
  const formatEngagementData = () => {
    if (!analytics?.aggregate_metrics) return [];
    
    const metrics = analytics.aggregate_metrics;
    const opened = metrics.total_emails_opened;
    const clicked = metrics.total_emails_clicked;
    const replied = metrics.total_emails_replied;
    const unopened = metrics.total_emails_sent - opened;
    
    return [
      { name: 'Unopened', value: unopened },
      { name: 'Opened (no click)', value: opened - clicked },
      { name: 'Clicked (no reply)', value: clicked - replied },
      { name: 'Replied', value: replied }
    ];
  };
  
  // Format campaign performance data
  const formatCampaignPerformance = () => {
    if (!analytics?.campaign_metrics?.open_rates) return [];
    
    return analytics.campaign_metrics.open_rates.slice(0, 5).map(campaign => ({
      name: campaign.campaign_name.length > 15 
        ? `${campaign.campaign_name.substring(0, 15)}...` 
        : campaign.campaign_name,
      openRate: parseFloat(campaign.open_rate.toFixed(2)),
      clickRate: analytics.campaign_metrics.click_rates.find(c => c.campaign_id === campaign.campaign_id)?.click_rate.toFixed(2) || 0,
      replyRate: analytics.campaign_metrics.reply_rates.find(c => c.campaign_id === campaign.campaign_id)?.reply_rate.toFixed(2) || 0
    }));
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-600">Error</h3>
        <p className="text-red-500">{error}</p>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="w-full p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-semibold text-yellow-600">No Data Available</h3>
        <p className="text-yellow-500">Start sending campaigns to generate analytics data.</p>
      </div>
    );
  }

  const metrics = analytics.aggregate_metrics;
  const dailyData = formatDailyData();
  const engagementData = formatEngagementData();
  const campaignPerformance = formatCampaignPerformance();

  const rotationOverview: RotationOverviewSummary | undefined = analytics.rotation_overview;
  const rotationCampaigns: RotationOverviewCampaign[] = rotationOverview?.campaigns || [];

  const handleTabChange = (e: React.MouseEvent<HTMLButtonElement>, newValue: number) => {
    setActiveTab(newValue);
    // Update URL query parameter
    const tabParam = newValue === 1 ? 'abtestresults' : 'overview';
    navigate(`/email/analytics?tab=${tabParam}`, { replace: true });
  };

  return (
    <div className="w-full p-4 space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Email Analytics Dashboard</h2>
      </div>
      
      <div className="border-b border-gray-200 mb-4">
        <div className="flex">
          <button 
            className={`py-2 px-4 font-medium ${activeTab === 0 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={(e) => handleTabChange(e, 0)}
            id="analytics-tab-0"
            aria-controls="analytics-tabpanel-0"
          >
            Overview
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 1 ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={(e) => handleTabChange(e, 1)}
            id="analytics-tab-1"
            aria-controls="analytics-tabpanel-1"
          >
            A/B Test Results
          </button>
          {/* Mailbox analytics lives in Overview; no extra tab needed */}
        </div>
      </div>
      
      <div role="tabpanel" hidden={activeTab !== 0} id="analytics-tabpanel-0" aria-labelledby="analytics-tab-0">
        {activeTab === 0 && (
          <>
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Emails Sent</h3>
          <p className="text-2xl font-bold text-gray-800">{metrics.total_emails_sent.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Average Open Rate</h3>
          <p className="text-2xl font-bold text-blue-600">{metrics.avg_open_rate}%</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Average Click Rate</h3>
          <p className="text-2xl font-bold text-green-600">{metrics.avg_click_rate}%</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Average Reply Rate</h3>
          <p className="text-2xl font-bold text-purple-600">{metrics.avg_reply_rate}%</p>
        </div>
      </div>
      
      {/* Daily Activity Chart */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily Email Activity</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dailyData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#8884d8" activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="opened" stroke="#82ca9d" />
              <Line type="monotone" dataKey="clicked" stroke="#ffc658" />
              <Line type="monotone" dataKey="replied" stroke="#ff7300" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Rotation Health (Mailbox Pools) */}
      {rotationOverview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Mailbox Rotation Health</h3>
            <p className="text-sm text-gray-500 mb-4">Daily overview of campaign mailbox pools and assignment status.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 p-3 rounded border border-blue-100">
                <p className="text-xs text-blue-700">Campaigns With Rotation</p>
                <p className="text-2xl font-bold text-blue-900">{rotationOverview.campaigns_with_rotation}/{rotationOverview.campaign_count}</p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-100">
                <p className="text-xs text-green-700">Active Assignments</p>
                <p className="text-2xl font-bold text-green-900">{rotationOverview.active_assignments}</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-100">
                <p className="text-xs text-yellow-700">Paused Assignments</p>
                <p className="text-2xl font-bold text-yellow-900">{rotationOverview.paused_assignments}</p>
              </div>
              <div className="bg-red-50 p-3 rounded border border-red-100">
                <p className="text-xs text-red-700">Exhausted Assignments</p>
                <p className="text-2xl font-bold text-red-900">{rotationOverview.exhausted_assignments}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                <p className="text-xs text-purple-700">Exhausted Campaigns</p>
                <p className="text-2xl font-bold text-purple-900">{rotationOverview.exhausted_campaigns}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded border border-orange-100">
                <p className="text-xs text-orange-700">Campaigns With Warnings</p>
                <p className="text-2xl font-bold text-orange-900">{rotationOverview.warning_campaigns}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Campaign Rotation Pools</h3>
            {rotationCampaigns.length ? (
              <div className="overflow-y-auto max-h-64">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Campaign</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Strategy</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Pool Size</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Health</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Manage</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rotationCampaigns.map((campaign: RotationOverviewCampaign) => (
                      <tr
                        key={campaign.campaign_id}
                        className={`hover:bg-gray-50 ${selectedRotationCampaignId === campaign.campaign_id ? 'bg-blue-50/40' : ''}`}
                      >
                        <td className="px-3 py-2">
                          <a href={`/email/campaigns/${campaign.campaign_id}`} className="text-blue-600 hover:underline">
                            {campaign.campaign_name}
                          </a>
                        </td>
                        <td className="px-3 py-2 capitalize text-gray-700">{campaign.strategy.replace('_', ' ')}</td>
                        <td className="px-3 py-2 text-gray-700">
                          {campaign.pool_size} pool / {campaign.active_assignments} active
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            campaign.health === 'healthy'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {campaign.health === 'healthy' ? 'Healthy' : 'Attention'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            className="text-sm px-3 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                            onClick={() => loadRotationHealth(campaign.campaign_id, campaign.campaign_name)}
                          >
                            Drill down
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No rotation-enabled campaigns yet.</p>
            )}
          </div>
        </div>
      )}

      {rotationCampaigns.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Rotation Mailbox Drill-down</h3>
              <p className="text-sm text-gray-500">
                Select a campaign above to pause, resume, rehab, or tune mailbox caps without leaving the dashboard.
              </p>
              {selectedRotationCampaignName && (
                <p className="text-sm text-gray-700 mt-1">
                  Managing pool: <span className="font-medium">{selectedRotationCampaignName}</span>
                </p>
              )}
            </div>
            {selectedRotationCampaignId && (
              <button
                className="self-start md:self-auto px-4 py-2 text-sm font-medium text-blue-700 border border-blue-200 rounded hover:bg-blue-50"
                onClick={() => loadRotationHealth(selectedRotationCampaignId, selectedRotationCampaignName)}
              >
                Refresh Pool
              </button>
            )}
          </div>

          {rotationHealthError && (
            <div className="mb-3 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
              {rotationHealthError}
            </div>
          )}
          {rotationActionMessage && (
            <div className="mb-3 p-3 rounded border border-green-200 bg-green-50 text-sm text-green-700">
              {rotationActionMessage}
            </div>
          )}

          {rotationHealthLoading && (
            <div className="w-full py-8 flex items-center justify-center text-sm text-gray-600">
              Loading mailbox pool…
            </div>
          )}

          {!rotationHealthLoading && !selectedRotationCampaignId && (
            <p className="text-sm text-gray-500">Select a campaign from the overview table to view its mailbox pool.</p>
          )}

          {!rotationHealthLoading && selectedRotationCampaignId && !rotationHealth && !rotationHealthError && (
            <p className="text-sm text-gray-500">Rotation health data is not available for this campaign yet.</p>
          )}

          {!rotationHealthLoading && rotationHealth && (
            <div className="space-y-4">
              {rotationHealth.warnings?.length > 0 && (
                <div className="p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-800">
                  <p className="font-medium">Warnings</p>
                  <ul className="list-disc list-inside space-y-1 mt-1">
                    {rotationHealth.warnings.map((warning, idx) => (
                      <li key={`warning-${idx}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Mailbox</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Sent Today</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Cap Remaining</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Depletion</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Actions</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Deliverability Trend</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {rotationHealth.mailboxes.map(mailbox => {
                      const assignmentId = mailbox.assignment_id;
                      const mutationBusy = assignmentId ? assignmentMutations[assignmentId] : false;
                      const statusLabel = mailbox.assignment_status || 'active';
                      const nextStatus = statusLabel === 'active' ? 'paused' : 'active';
                      const nextLabel = statusLabel === 'active' ? 'Pause' : statusLabel === 'paused' ? 'Resume' : 'Rehab';
                      const capInputDisabled = !assignmentId || mutationBusy;
                      const capDraft = assignmentId ? capDrafts[assignmentId] ?? '' : '';
                      const depletionCopy = mailbox.depletion_forecast_days !== null && mailbox.depletion_forecast_days !== undefined
                        ? `${mailbox.depletion_forecast_days} day${mailbox.depletion_forecast_days === 1 ? '' : 's'}`
                        : '—';
                      const deliverabilityPoints = mailbox.deliverability_trend?.slice(-4) || [];

                      return (
                        <tr key={`${mailbox.mailbox_id}-${assignmentId || 'default'}`}>
                          <td className="px-3 py-3">
                            <div className="font-medium text-gray-900">{mailbox.mailbox_name}</div>
                            <div className="text-xs text-gray-500">{mailbox.mailbox_email}</div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              statusLabel === 'active'
                                ? 'bg-green-100 text-green-800'
                                : statusLabel === 'paused'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}>
                              {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
                            </span>
                            {mailbox.warning && (
                              <p className="text-xs text-amber-700 mt-1">{mailbox.warning}</p>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <p className="text-sm text-gray-700">{mailbox.campaign_sent_today} campaign</p>
                            <p className="text-xs text-gray-500">{mailbox.sent_today} total</p>
                          </td>
                          <td className="px-3 py-3">
                            {mailbox.cap_remaining !== null ? (
                              <span className={mailbox.cap_remaining <= 2 ? 'text-red-600 font-semibold' : 'text-gray-800'}>
                                {mailbox.cap_remaining}
                              </span>
                            ) : 'Unlimited'}
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm text-gray-700">{depletionCopy}</span>
                          </td>
                          <td className="px-3 py-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <button
                                className={`px-3 py-1 text-xs font-semibold rounded border ${mutationBusy ? 'border-gray-200 text-gray-400' : 'border-blue-200 text-blue-700 hover:bg-blue-50'}`}
                                disabled={!assignmentId || mutationBusy}
                                onClick={() => handleAssignmentStatusChange(mailbox, nextStatus)}
                              >
                                {nextLabel}
                              </button>
                              <div className="text-[11px] text-gray-400" title={!assignmentId ? 'Default mailbox fallback cannot be controlled here' : ''}>
                                {mutationBusy ? 'Saving…' : assignmentId ? '' : 'N/A'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min={0}
                                className="w-20 rounded border-gray-300 text-xs focus:border-blue-500 focus:ring-blue-500"
                                disabled={capInputDisabled}
                                value={capDraft}
                                onChange={(e) => assignmentId && handleCapDraftChange(assignmentId, e.target.value)}
                                placeholder="Cap"
                              />
                              <button
                                className={`px-2 py-1 text-xs rounded ${capInputDisabled ? 'bg-gray-100 text-gray-400' : 'bg-gray-900 text-white hover:bg-gray-700'}`}
                                disabled={capInputDisabled}
                                onClick={() => handleDailyCapSave(mailbox)}
                              >
                                Save
                              </button>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            {deliverabilityPoints.length ? (
                              <div className="text-xs text-gray-600 space-y-1">
                                {deliverabilityPoints.map(point => (
                                  <div key={`${mailbox.mailbox_id}-${point.date}`} className="flex justify-between">
                                    <span>{format(new Date(point.date), 'MMM dd')}</span>
                                    <span>{point.sent} sent / {point.replied} replies</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No trend data</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Engagement Breakdown and Campaign Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Engagement Breakdown */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Engagement Breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={engagementData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {engagementData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} emails`, 'Count']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Campaign Performance */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Campaign Performance</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={campaignPerformance}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}%`, 'Rate']} />
                <Legend />
                <Bar dataKey="openRate" name="Open Rate" fill="#8884d8" />
                <Bar dataKey="clickRate" name="Click Rate" fill="#82ca9d" />
                <Bar dataKey="replyRate" name="Reply Rate" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Deliverability Monitoring */}
      <div className="mt-4">
        <EmailDeliverabilityPanel />
      </div>

      {/* Domain Health */}
      <div className="mt-4">
        <DomainHealthPanel />
      </div>

      {/* Custom Tracking Domains */}
      <div className="mt-4">
        <TrackingDomainsPanel />
      </div>

      {/* Adaptive Warmup Health */}
      {selectedMailboxId && (
        <div className="mt-4">
          <WarmupHealthPanel mailboxId={selectedMailboxId} />
        </div>
      )}

      {/* A/B Testing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* A/B Testing Stats */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">A/B Test Performance</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">Active Tests</p>
              <p className="text-2xl font-bold text-blue-800">{analytics.ab_test_metrics?.active_tests || 0}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-100">
              <p className="text-sm text-green-700">Completed Tests</p>
              <p className="text-2xl font-bold text-green-800">{analytics.ab_test_metrics?.completed_tests || 0}</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-700">Avg. Open Rate Lift</p>
              <p className="text-2xl font-bold text-purple-800">{analytics.ab_test_metrics?.avg_lift?.open_rate || 0}%</p>
            </div>
            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
              <p className="text-sm text-indigo-700">Avg. Click Rate Lift</p>
              <p className="text-2xl font-bold text-indigo-800">{analytics.ab_test_metrics?.avg_lift?.click_rate || 0}%</p>
            </div>
          </div>
        </div>
        
        {/* Recent A/B Tests */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent A/B Tests</h3>
          <div className="overflow-y-auto max-h-64">
            {analytics.ab_tests && analytics.ab_tests.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {analytics.ab_tests.slice(0, 5).map((test) => (
                  <li key={test.id} className="py-3">
                    <div className="flex justify-between">
                      <a href={`/email/ab-tests/${test.id}`} className="text-blue-600 font-medium hover:underline">
                        {test.name}
                      </a>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        test.status === 'active' ? 'bg-green-100 text-green-800' :
                        test.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        test.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        test.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {test.status.charAt(0).toUpperCase() + test.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Campaign: {test.campaign_name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {format(new Date(test.created_at), 'MMM dd, yyyy')}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-500 py-4">No A/B tests found</p>
            )}
          </div>
          <div className="mt-3 text-right">
            <a href="/email/campaigns/new" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Create new A/B test →
            </a>
          </div>
        </div>
      </div>
      
      {/* Recent Campaigns Table */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Campaigns</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.recent_campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:underline">
                    <a href={`/email/campaigns/${campaign.id}`}>{campaign.name}</a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                      campaign.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                      campaign.status === 'paused' ? 'bg-orange-100 text-orange-800' :
                      campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(campaign.created_at), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(campaign.updated_at), 'MMM dd, yyyy')}
                  </td>
                </tr>
              ))}
              {analytics.recent_campaigns.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    No campaigns found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
            </div>

            {/* Mailbox Deliverability and Reply Intent */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              {/* Mailbox Selector and Rates */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Mailbox Deliverability (30d)</h3>
                {premiumBlocked && (
                  <div className="mb-3 p-3 rounded border border-yellow-200 bg-yellow-50 text-sm text-yellow-800">
                    Premium required to view analytics. <a href="/upgrade" className="underline text-yellow-900">Upgrade</a>
                  </div>
                )}
                <div className="mb-3">
                  <label htmlFor="mailboxSelect" className="block text-sm font-medium text-gray-700 mb-1">Select Mailbox</label>
                  <select
                    id="mailboxSelect"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    value={selectedMailboxId}
                    onChange={(e) => setSelectedMailboxId(e.target.value)}
                  >
                    {mailboxes.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                    ))}
                  </select>
                </div>
                {mailboxLoading ? (
                  <div className="text-sm text-gray-500">Loading mailbox analytics…</div>
                ) : mailboxAnalytics ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 p-3 rounded border border-blue-100">
                      <p className="text-xs text-blue-700">Open Rate</p>
                      <p className="text-xl font-bold text-blue-800">{mailboxAnalytics.rates.open_rate.toFixed(1)}%</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded border border-green-100">
                      <p className="text-xs text-green-700">Click Rate</p>
                      <p className="text-xl font-bold text-green-800">{mailboxAnalytics.rates.click_rate.toFixed(1)}%</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded border border-purple-100">
                      <p className="text-xs text-purple-700">Reply Rate</p>
                      <p className="text-xl font-bold text-purple-800">{mailboxAnalytics.rates.reply_rate.toFixed(1)}%</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded border border-red-100">
                      <p className="text-xs text-red-700">Bounce Rate</p>
                      <p className="text-xl font-bold text-red-800">{mailboxAnalytics.rates.bounce_rate.toFixed(1)}%</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No analytics available</p>
                )}
              </div>

              {/* Reply Intent Distribution */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200 lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Reply Intent Distribution (30d)</h3>
                {premiumBlocked && (
                  <div className="mb-3 p-3 rounded border border-yellow-200 bg-yellow-50 text-sm text-yellow-800">
                    Premium required to view analytics. <a href="/upgrade" className="underline text-yellow-900">Upgrade</a>
                  </div>
                )}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    {intentLoading ? (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">Loading…</div>
                    ) : (
                      <PieChart>
                        <Pie
                          data={(intentDist ? Object.entries(intentDist.distribution) : []).map(([k, v]) => ({ name: k, value: v }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={90}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {(intentDist ? Object.entries(intentDist.distribution) : []).map((_, index) => (
                            <Cell key={`intent-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} replies`, 'Count']} />
                        <Legend />
                      </PieChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Warmup Telemetry */}
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200 lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Warmup Telemetry (30d)</h3>
                {premiumBlocked && (
                  <div className="mb-3 p-3 rounded border border-yellow-200 bg-yellow-50 text-sm text-yellow-800">
                    Premium required to view analytics. <a href="/upgrade" className="underline text-yellow-900">Upgrade</a>
                  </div>
                )}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    {warmupLoading ? (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">Loading…</div>
                    ) : (
                      <LineChart data={(warmupTelemetry?.series || [])} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="warmup_sent" name="Warmup Sent" stroke="#10B981" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="errors" name="Errors" stroke="#EF4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <div role="tabpanel" hidden={activeTab !== 1} id="analytics-tabpanel-1" aria-labelledby="analytics-tab-1">
        {activeTab === 1 && <ABTestResults />}
      </div>
    </div>
  );
};

export default EmailAnalyticsDashboard;
